import * as React from 'react'

import { InfoIcon, PlusIcon, SlidersVertical } from 'lucide-react'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'

import { BicCompletionMode, IWorkflowStep } from '~/client/graph'
import BallInCourtRulesTable from '~/client/src/desktop/components/BallInCourtRulesTable/BallInCourtRulesTable'
import { Button } from '~/client/src/shared/components/Button/Button'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import { FieldTrigger } from '~/client/src/shared/components/FieldTrigger'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/client/src/shared/components/shadcn-ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import OperationRule from '~/client/src/shared/models/OperationRule'
import User from '~/client/src/shared/models/User'
import {
  getBicCompletionMode,
  getBicCompletionModeLabel,
} from '~/client/src/shared/models/WorkflowType'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import PermitBallInCourtStore from '~/client/src/shared/stores/ui/PermitBallInCourt.store'
import { isEqualDeep } from '~/client/src/shared/utils/util'

// localization: translated

interface IProps {
  workflowStep: IWorkflowStep
  stepPosition: number
  stepName: string
  formTypeId: string
  isUpdating: boolean
  allWorkflowSteps?: IWorkflowStep[]
  removingRuleId: string
  newRuleId?: string

  getUsersFromAssignment(assignmentExpressions: string[]): User[]
  getStepRules(ruleIds: string[]): OperationRule[]
  onRemoveRuleClick(ruleId: string): void
  onRemoveRuleConfirm(): void
  onRemoveRuleCancel(): void
  onDuplicateRuleClick(ruleId: string): void
  onChangeUserForRule(
    rule: OperationRule,
    userId: string,
    isNew?: boolean,
  ): void
  onRuleExpressionChange(
    rule: OperationRule,
    locationAndCompExpression: string,
  ): void
  onAddNewRuleClick(stepId: string): void
  onFieldToggleForRule?(
    rule: OperationRule,
    fieldId: string,
    isSelected: boolean,
  ): void
  onFieldsToggleForRule?(
    rule: OperationRule,
    fieldIds: string[],
    isSelected: boolean,
  ): void
  onDiffCommitForRule?(
    rule: OperationRule,
    addedUserIds: string[],
    removedUserIds: string[],
    addedFieldIds: string[],
    removedFieldIds: string[],
    addedCompanyIds: string[],
    removedCompanyIds: string[],
  ): void
  onBicCompletionModeChange(stepId: string, mode: BicCompletionMode): void
  onRenameRule(rule: OperationRule, name: string): void
  onCompanyDiffCommitForRule(
    rule: OperationRule,
    addedCompanyIds: string[],
    removedCompanyIds: string[],
  ): void

  permitBallInCourtStore: PermitBallInCourtStore

  tagsStore?: TagsStore
}

@inject('tagsStore')
@observer
export default class WorkflowStepRulesDialog extends React.Component<IProps> {
  @observable private permitBallInCourtStore: PermitBallInCourtStore = null
  @observable private formTypeId: string = null
  @observable private workflowStep: IWorkflowStep = null
  @observable private isUpdating: boolean = null
  @observable private isDialogOpen: boolean = false

  public constructor(props: IProps) {
    super(props)
    this.formTypeId = props.formTypeId
    this.workflowStep = props.workflowStep
    this.isUpdating = props.isUpdating
    this.permitBallInCourtStore = props.permitBallInCourtStore
    makeObservable(this)
  }

  public componentDidUpdate(): void {
    runInAction(() => {
      if (this.formTypeId !== this.props.formTypeId) {
        this.formTypeId = this.props.formTypeId
      }
      if (this.isUpdating !== this.props.isUpdating) {
        this.isUpdating = this.props.isUpdating
      }
      if (!isEqualDeep(this.workflowStep, this.props.workflowStep)) {
        this.workflowStep = this.props.workflowStep
      }
    })
  }

  public render() {
    const { stepPosition, stepName, isUpdating, tagsStore } = this.props

    return (
      <Dialog
        open={this.isDialogOpen}
        onOpenChange={this.setDialogOpen}
        disablePointerDismissal
      >
        <DialogTrigger render={<FieldTrigger icon={<SlidersVertical />} />}>
          {this.title}
        </DialogTrigger>
        <DialogContent
          showCloseButton={false}
          className="tw:flex tw:max-h-[85vh] tw:w-[720px] tw:flex-col tw:gap-0 tw:overflow-hidden tw:p-0 tw:sm:max-w-[min(720px,calc(100%-2rem))]"
        >
          <DialogHeader className="tw:border-b tw:border-border tw:px-5 tw:py-4">
            <DialogTitle>{Localization.translator.bicUser}</DialogTitle>
            <DialogDescription>
              {`${Localization.translator.step} ${stepPosition}: ${stepName}`}
            </DialogDescription>
          </DialogHeader>

          <div className="tw:relative tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
            {isUpdating && (
              <div className="tw:absolute tw:inset-0 tw:z-10 tw:bg-background/60" />
            )}
            {this.renderAssignmentCaption()}
            {this.renderCompletionModeSelect()}
            <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto">
              {this.renderRulesTable()}
            </div>
          </div>

          <DialogFooter className="tw:mx-0 tw:mb-0 tw:justify-between tw:px-5 tw:py-3 tw:sm:justify-between">
            <Button
              variant="ghost"
              disabled={!tagsStore.isDataReceived || isUpdating}
              onClick={this.onAddNewRuleClick}
            >
              <PlusIcon data-icon="inline-start" />
              {Localization.translator.addRule}
            </Button>
            <DialogClose render={<Button />}>
              {Localization.translator.done}
            </DialogClose>
          </DialogFooter>

          {this.renderRemoveRuleConfirmDialog()}
        </DialogContent>
      </Dialog>
    )
  }

  private renderAssignmentCaption(): JSX.Element {
    return (
      <div className="tw:flex tw:items-center tw:gap-2 tw:px-5 tw:pt-4">
        <span className="tw:text-app-body-medium tw:text-foreground">
          {Localization.translator.thisWorkflowStepIsAssignedByRules}:
        </span>
        <Tooltip>
          <TooltipTrigger className="tw:inline-flex tw:text-muted-foreground">
            <InfoIcon size={16} />
          </TooltipTrigger>
          <TooltipContent>
            {Localization.translator.adminsCanAdvanceWorkflow}
          </TooltipContent>
        </Tooltip>
      </div>
    )
  }

  private renderCompletionModeSelect(): JSX.Element {
    const { workflowStep, onBicCompletionModeChange } = this.props
    const hasBicRules = workflowStep.workflowRuleIds?.length > 0
    const completionMode = getBicCompletionMode(workflowStep)
    const options = this.completionModeOptions

    return (
      <div className="tw:flex tw:flex-col tw:gap-1 tw:border-b tw:border-border tw:px-5 tw:py-3">
        <div className="tw:flex tw:items-center tw:gap-2">
          <span className="tw:text-app-body tw:text-foreground">
            {Localization.translator.completedBy}
          </span>
          <div className="tw:ml-auto tw:flex">
            <Tooltip>
              <TooltipTrigger render={<span className="tw:inline-flex" />}>
                <Select
                  value={completionMode}
                  disabled={!hasBicRules}
                  onValueChange={value =>
                    onBicCompletionModeChange(
                      workflowStep.id,
                      value as BicCompletionMode,
                    )
                  }
                  items={options}
                >
                  <SelectTrigger
                    aria-label={Localization.translator.completedBy}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent align="end">
                    {options.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TooltipTrigger>
              {!hasBicRules && (
                <TooltipContent>
                  {Localization.translator.allUsersMustCompleteDisabledTooltip}
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        </div>
        <p className="tw:text-app-meta tw:text-muted-foreground">
          {this.getCompletionModeDescription(completionMode)}
        </p>
      </div>
    )
  }

  private getCompletionModeDescription(mode: BicCompletionMode): string {
    const { translator } = Localization

    switch (mode) {
      case BicCompletionMode.AllUsers:
        return translator.allAssignedUsersDescription
      case BicCompletionMode.OnePerRule:
        return translator.oneUserPerRuleDescription
      default:
        return translator.anyAssignedUserDescription
    }
  }

  private renderRulesTable(): JSX.Element {
    const {
      getUsersFromAssignment,
      onRemoveRuleClick,
      onDuplicateRuleClick,
      onChangeUserForRule,
      onRuleExpressionChange,
      onFieldToggleForRule,
      onFieldsToggleForRule,
      onDiffCommitForRule,
      onRenameRule,
      onCompanyDiffCommitForRule,
      allWorkflowSteps,
      newRuleId,
    } = this.props

    return (
      <BallInCourtRulesTable
        bicOperationRules={this.bicRules}
        newRuleId={newRuleId}
        allWorkflowSteps={allWorkflowSteps}
        getUsersFromAssignment={getUsersFromAssignment}
        onRemoveRuleClick={onRemoveRuleClick}
        onDuplicateRuleClick={onDuplicateRuleClick}
        onChangeUserForRule={onChangeUserForRule}
        onRuleExpressionChange={onRuleExpressionChange}
        onFieldToggleForRule={onFieldToggleForRule}
        onFieldsToggleForRule={onFieldsToggleForRule}
        onDiffCommitForRule={onDiffCommitForRule}
        onRenameRule={onRenameRule}
        onCompanyDiffCommitForRule={onCompanyDiffCommitForRule}
      />
    )
  }

  // Rendered inside the dialog so it nests: Base UI then keeps this dialog
  // open, and its Escape disabled, while the confirm is up.
  private renderRemoveRuleConfirmDialog(): JSX.Element {
    const { removingRuleId, onRemoveRuleConfirm, onRemoveRuleCancel } =
      this.props

    return (
      <ConfirmDialog
        useRedConfirmationButton
        title={Localization.translator.deleteRule}
        isOpen={!!removingRuleId}
        onCancelClicked={onRemoveRuleCancel}
        onDoneClicked={onRemoveRuleConfirm}
        doneTitle={Localization.translator.delete}
      >
        <div className="tw:text-app-body tw:whitespace-pre-line">
          {Localization.translator.shouldDeleteRule}?
        </div>
      </ConfirmDialog>
    )
  }

  private get bicRules(): OperationRule[] {
    const { getNewOperationRule } = this.permitBallInCourtStore

    const rules = this.props.getStepRules(this.workflowStep.workflowRuleIds)
    const isNewRuleShown =
      this.workflowStep.workflowRuleIds?.includes(OperationRule.NEW_ID) &&
      !rules.some(r => !r.isExisting)

    return (
      isNewRuleShown ? [...rules, getNewOperationRule(this.formTypeId)] : rules
    ).sort((a, b) => a.createdAt - b.createdAt)
  }

  private get completionModeOptions(): {
    value: BicCompletionMode
    label: string
  }[] {
    return [
      BicCompletionMode.AnyOne,
      BicCompletionMode.AllUsers,
      BicCompletionMode.OnePerRule,
    ].map(value => ({ value, label: getBicCompletionModeLabel(value) }))
  }

  @computed
  private get title(): string {
    const { workflowStep } = this
    const ruleIds = workflowStep?.workflowRuleIds

    if (!ruleIds?.length) {
      return Localization.translator.none
    }

    return Localization.translator.xRules(ruleIds.length)
  }

  private setDialogOpen = (open: boolean) => {
    runInAction(() => {
      this.isDialogOpen = open
    })
  }

  private onAddNewRuleClick = () => {
    this.props.onAddNewRuleClick(this.workflowStep?.id)
  }
}
